import { useState } from 'react'
import { X, AlertCircle, CheckCircle } from 'lucide-react'
import { SecureInput } from './SecureInput'
import { scanInput } from '../lib/inputSecurity'

export function CreateAccountModal({ isOpen, onClose, onSwitchToSignIn }) {
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  if (!isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    
    // Validation
    if (!name.trim() || !email.trim() || !password.trim()) {
      setError('Please fill in all required fields')
      return
    }
    const secErr = scanInput(name) || scanInput(company)
    if (secErr) { setError(secErr); return }
    
    if (!agreedToTerms) {
      setError('Please confirm you are not a robot')
      return
    }

    setIsLoading(true)
    
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'
      
      const response = await fetch(`${API_URL}/api/register/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          password: password,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        })
      })

      const data = await response.json()

      if (!response.ok) {
        const errorMessage = data.errors?.email?.[0] || 
                           data.errors?.username?.[0] || 
                           data.message || 
                           'Registration failed'
        throw new Error(errorMessage)
      }

      setSuccess(true)
      setName('')
      setCompany('')
      setEmail('')
      setPassword('')
      setPhone('')
      setAgreedToTerms(false)
      
      // Show success and redirect to sign in after 2 seconds
      setTimeout(() => {
        onSwitchToSignIn()
      }, 2000)
    } catch (err) {
      console.error('[Registration] Error:', err)
      setError(err.message || 'Registration failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition z-10"
        >
          <X className="w-6 h-6" />
        </button>
        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-3">
          {/* Title */}
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Create an Account</h2>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start gap-3">
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-sm">Account created successfully! Redirecting to login...</p>
              </div>
            </div>
          )}

          {/* Name Field */}
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold text-gray-800 w-20 flex-shrink-0">
              Name
            </label>
            <SecureInput
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:border-[#358ECD] focus:ring-1 focus:ring-[#358ECD] text-gray-700 placeholder-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
            />
          </div>

          {/* Company Field */}
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold text-gray-800 w-20 flex-shrink-0">
              Company
            </label>
            <SecureInput
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Company name"
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:border-[#358ECD] focus:ring-1 focus:ring-[#358ECD] text-gray-700 placeholder-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* Email Field */}
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold text-gray-800 w-20 flex-shrink-0">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:border-[#358ECD] focus:ring-1 focus:ring-[#358ECD] text-gray-700 placeholder-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
            />
          </div>

          {/* Password Field */}
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold text-gray-800 w-20 flex-shrink-0">
              Password
            </label>
            <div className="flex-1 relative">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                disabled={isLoading}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:border-[#358ECD] focus:ring-1 focus:ring-[#358ECD] text-gray-700 placeholder-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                required
              />
            </div>
          </div>

          {/* Phone Field */}
          <div className="flex items-center gap-4">
            <label className="text-sm font-semibold text-gray-800 w-20 flex-shrink-0">
              Phone
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Mobile phone for 2FA"
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:border-[#358ECD] focus:ring-1 focus:ring-[#358ECD] text-gray-700 placeholder-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {/* reCAPTCHA */}
          <div className="flex items-center gap-3 pt-2 pb-2">
            <input
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              disabled={isLoading}
              className="w-4 h-4 border border-gray-300 rounded accent-[#358ECD] disabled:cursor-not-allowed"
            />
            <span className="text-xs text-gray-600">I'm not a robot</span>
          </div>

          {/* Create Account Button */}
          <button
            type="submit"
            disabled={isLoading || success}
            className="w-full bg-[#358ECD] text-white font-bold py-3 px-4 rounded-lg hover:bg-[#2a6fa3] transition mt-5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Creating Account...' : 'CREATE ACCOUNT'}
          </button>

          {/* Sign In Link */}
          <div className="text-center pt-3">
            <p className="text-sm text-gray-700">
              Already have an account?{' '}
              <button
                type="button"
                onClick={onSwitchToSignIn}
                className="text-[#358ECD] hover:text-[#2a6fa3] font-medium transition"
              >
                Sign In
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}
